var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Module } from "../module";
import { LayoutCreationFinishedMessage, UpdateFacetOnModelMessage, BusyIndicator, RoomChangedMessage, StartRoomTransitionMessage, ModuleId, ServerRequestErrorMessage, ToastMessage, CancelLoadRoomMessage, LayoutFacet, RemoveSubModelMessage, DestroyLayoutMessage, InitLayoutMessage, CreateLayoutMessage, AvailableLayoutersMessage, CollectLayoutScriptInfosMessage, AddLayoutScriptToCurrentRoomMessage, ResponseLayoutScriptInfosMessage, AskOkMessage, AddMeshLayouterToCurrentRoomMessage, ClearRoomMessage, ResponseGLTFLayouterMessage, LoadGLTFLayouterMessage } from "flavr-common";
import { busService } from "../bus";
import { UserAuthenticatedMessage } from "../../messages/session/SessionMessages";
import { modelPool } from "@modules/model";
/**
 * Orchestrates room layout creation so a room can be populated from layout facets, layouter scripts and authenticated user capabilities.
 *
 * Facets:
 * - `LayoutFacet` - Stores the layout parameters that describe how a room should be populated.
 *
 * Messages:
 * - `CreateLayoutMessage`, `InitLayoutMessage`, `DestroyLayoutMessage`, `StartRoomTransitionMessage` and `RoomChangedMessage` - Drive the layout lifecycle for a room.
 * - `UpdateFacetOnModelMessage` with `LayoutFacet` - Updates cached layout parameters for a room.
 * - `CollectLayoutScriptInfosMessage`, `AddLayoutScriptToCurrentRoomMessage` and `AddMeshLayouterToCurrentRoomMessage` - Are used to discover or apply layout-related scripts.
 * - `UserAuthenticatedMessage` - Refreshes the set of layouters that the current user is allowed to use.
 * - `RemoveSubModelMessage` - Clears a room's cached layout parameters once it has actually been removed (guarded by timestamp).
 */
export class LayouterModule extends Module {
    /**
     * Subscribes to room removal and user-authentication messages, wires up the ask/answer handlers for discovering and
     * applying layout scripts, and opens a second bus connection dedicated to the layout/room life-cycle messages
     * (`CreateLayoutMessage`, `InitLayoutMessage`, `DestroyLayoutMessage`, `StartRoomTransitionMessage`,
     * `RoomChangedMessage` and `LayoutFacet` updates), dispatching each to its corresponding handler.
     */
    constructor() {
        super(ModuleId.LAYOUT);
        this.userRoles = [];
        this.layouters = new Map();
        this.layoutParameterMap = new Map();
        this.onRemoveSubModelMessage = (msg, meta) => {
            const existing = this.layoutParameterMap.get(msg.uid);
            if (!existing || existing.timeStamp >= meta.timeStamp)
                return;
            this.layoutParameterMap.delete(msg.uid);
        };
        this.onUserAuthenticatedMessage = (msg) => {
            this.userRoles = msg.roles;
            this.sendAvailebleLayoutersMessage();
        };
        this.handleCollectLayoutScriptInfosMessage = (_msg, askId) => {
            const infos = [];
            const currentRoomLayouter = this.currentRoomModel !== undefined ? this.getLayouterForRoom(this.currentRoomModel) : undefined;
            for (const [layoutId, layouter] of this.layouters) {
                const scriptInfo = layouter.constructor.scriptInfo;
                if (scriptInfo === undefined)
                    continue;
                const layoutName = layouter.layoutInfo !== undefined ? layouter.layoutInfo.name : layoutId;
                const inCurrentRoom = currentRoomLayouter === layouter ? true : undefined;
                infos.push({
                    layoutId,
                    layoutName,
                    inCurrentRoom
                });
            }
            this.connection.answer(askId, new ResponseLayoutScriptInfosMessage(infos));
        };
        // private readonly handleRequestPredefinedLayoutScriptMessage = (msg: RequestPredefinedLayoutScriptMessage, askId: string): void => {
        //   const layouter = this.layouters.get(msg.layouterId)
        //
        //   let script: string | undefined
        //
        //   if (layouter !== undefined) {
        //     const scriptInfo = (layouter.constructor as any).scriptInfo as LayouterScriptInfo
        //     if (scriptInfo !== undefined) {
        //       script = scriptInfo.predefinedScript
        //     }
        //   }
        //
        //   this.connection.answer(askId, new ResponsePredefinedLayoutScriptMessage(msg.layouterId, script))
        // }
        this.handleAddLayoutScriptToCurrentRoomMessage = (msg, askId) => {
            if (this.currentRoomModel === undefined)
                throw new Error("You are not in a room");
            const layouter = this.getLayouterForRoom(this.currentRoomModel);
            if (layouter === undefined)
                throw new Error(`No layouter defined for current room ${this.currentRoomModel.getName()}`);
            if (msg.layouterId !== undefined && msg.layouterId !== layouter.layouterId) {
                throw new Error(`expected add script for layouter id ${msg.layouterId}, but current room has layout id ${layouter.layouterId}`);
            }
            if (layouter.setRoomScript === undefined) {
                throw new Error(`Scripts are not supported for layouter ${layouter.layouterId}`);
            }
            const root = this.currentRoomModel;
            const scriptMessages = [];
            layouter.setRoomScript(root, msg.scriptId, scriptMessages);
            this.connection.sendMultiple(scriptMessages);
            if (askId !== undefined) {
                this.connection.answer(askId, new AskOkMessage());
            }
        };
        this.handleAddMeshLayouterToCurrentRoomMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            if (this.currentRoomModel === undefined)
                throw new Error("You are not in a room");
            yield this.connection.askByMessageType(new ClearRoomMessage(this.currentRoomModel.getUID()), AskOkMessage);
            const response = yield this.connection.askByMessageType(new LoadGLTFLayouterMessage(this.currentRoomModel.getUID(), msg.url), ResponseGLTFLayouterMessage);
            this.connection.sendMultiple(response.messages);
            if (askId !== undefined) {
                this.connection.answer(askId, new AskOkMessage());
            }
        });
        this.connection.subscribeByType(RemoveSubModelMessage, this.onRemoveSubModelMessage);
        this.connection.subscribeByType(UserAuthenticatedMessage, this.onUserAuthenticatedMessage);
        this.connection.subscribeForAskRequest(CollectLayoutScriptInfosMessage, this.handleCollectLayoutScriptInfosMessage);
        this.connection.subscribeByTypeOrAskRequest(AddLayoutScriptToCurrentRoomMessage, this.handleAddLayoutScriptToCurrentRoomMessage);
        // this.connection.subscribeForAskRequest(RequestPredefinedLayoutScriptMessage, this.handleRequestPredefinedLayoutScriptMessage)
        this.connection.subscribeByTypeOrAskRequest(AddMeshLayouterToCurrentRoomMessage, this.handleAddMeshLayouterToCurrentRoomMessage);
        // we use a second connection for our subscription so we can receive UpdateFacetOnModelMessages
        // that were sens via our main connection
        busService
            .connection(this.moduleId + "subscribe")
            .subscribe([
            CreateLayoutMessage.typestatic,
            InitLayoutMessage.typestatic,
            DestroyLayoutMessage.typestatic,
            StartRoomTransitionMessage.typestatic,
            RoomChangedMessage.typestatic,
            `${UpdateFacetOnModelMessage.typestatic}${LayoutFacet.typestatic}`
        ], (msg, meta) => {
            if (CreateLayoutMessage.is(msg)) {
                this.onCreateLayoutMessage(msg);
            }
            else if (InitLayoutMessage.is(msg)) {
                this.onInitLayoutMessage(msg);
            }
            else if (DestroyLayoutMessage.is(msg)) {
                this.onDestroyLayoutMessage(msg);
            }
            else if (StartRoomTransitionMessage.is(msg)) {
                const room = modelPool.getModel(msg.uid);
                if (!room)
                    return;
                this.onExitRoom(room);
            }
            else if (RoomChangedMessage.is(msg)) {
                const room = modelPool.getModel(msg.uid);
                if (!room)
                    return;
                this.onEnterRoom(room);
            }
            else if (UpdateFacetOnModelMessage.is(msg)) {
                if (LayoutFacet.is(msg.facet)) {
                    this.layoutParameterMap.set(msg.uid, {
                        params: msg.facet.params,
                        timeStamp: meta.timeStamp
                    });
                }
            }
            else {
                console.warn("LayouterModule module got unexpected message....", msg);
            }
        });
    }
    /**
     * Sends a request message over the module connection and resolves with the single expected response, turning server-side request errors into rejected promises.
     */
    requestData(requestMessage) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                this.connection.ask(requestMessage, (messages) => {
                    if (messages.length === 1) {
                        const answer = messages[0];
                        if (ServerRequestErrorMessage.is(answer)) {
                            reject(new Error(`Server Error: ${answer.flag}`));
                        }
                        else {
                            resolve(answer);
                        }
                    }
                    else {
                        reject(new Error(`Got ${messages.length} answer messages (expected 1).`));
                    }
                });
            });
        });
    }
    /**
     * Returns the module connection so layouters can reuse the same messaging channel for follow-up requests.
     */
    getConnection() {
        return this.connection;
    }
    /**
     * Keeps the standard module life-cycle entry point even though the layouter subscriptions are registered in the constructor.
     */
    init() { }
    sendAvailebleLayoutersMessage() {
        const layouters = {};
        for (const layouter of this.layouters.values()) {
            if (layouter.layoutInfo && this.hasRequiredRolesForLayout(layouter.layoutInfo)) {
                layouters[layouter.layouterId] = layouter.layoutInfo;
            }
        }
        this.connection.send(new AvailableLayoutersMessage(layouters));
    }
    /**
     * Registers a layouter implementation and links it back to this module so it can participate in future room layout creation.
     */
    addLayouter(layout) {
        this.layouters.set(layout.layouterId, layout);
        layout.layoutModule = this;
    }
    onCreateLayoutMessage(msg) {
        var _a;
        const roomModel = modelPool.getModel(msg.uid);
        if (roomModel === undefined) {
            throw new Error("RoomId not found");
        }
        const data = this.layoutParameterMap.get(roomModel.getUID());
        const layouterParams = data ? data.params : undefined;
        const layouter = this.getLayouterForRoom(roomModel, layouterParams);
        if (!layouter || (layouter.layoutInfo && !this.hasRequiredRolesForLayout(layouter.layoutInfo))) {
            this.connection.send(new CancelLoadRoomMessage(roomModel.getUID()));
            this.connection.send(new ToastMessage(`Unknown layout for room ${roomModel.getName()}`));
            return;
        }
        if (layouter.isShowingBusyIndicator) {
            this.connection.send(new UpdateFacetOnModelMessage(msg.uid, new BusyIndicator(0.0, true)));
        }
        const room = (roomModel.isRoom() ? roomModel : undefined);
        const root = (_a = room) !== null && _a !== void 0 ? _a : roomModel;
        const layoutCreationMessages = [];
        layouter
            .createLayout(root, room, layouterParams, layoutCreationMessages)
            .then((subLayouters) => __awaiter(this, void 0, void 0, function* () {
            this.connection.sendMultiple(layoutCreationMessages);
            yield this.createSubLayouts(subLayouters);
        }))
            .then(() => {
            if (layouter.isShowingBusyIndicator) {
                this.connection.send(new UpdateFacetOnModelMessage(msg.uid, new BusyIndicator(1.0, true)));
            }
            this.connection.send(new LayoutCreationFinishedMessage(layouter.layouterId, msg.uid));
        })
            .catch((error) => {
            this.connection.send(new CancelLoadRoomMessage(roomModel.getUID()));
            const errorText = `Couldn't create layout: ${String(error)}`;
            this.connection.send(new ToastMessage(errorText));
            console.error(errorText);
        });
    }
    hasRequiredRolesForLayout(layouteInfo) {
        for (const role of layouteInfo.requiredRoles) {
            if (!this.userRoles.includes(role)) {
                return false;
            }
        }
        return true;
    }
    createSubLayouts(subLayouts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (subLayouts === undefined || subLayouts.length === 0)
                return;
            for (const layout of subLayouts) {
                const layouter = this.layouters.get(layout.params.layouterId);
                if (layouter !== undefined) {
                    const subSubLayoutMessages = [];
                    const subSubLayouts = yield layouter.createLayout(layout.rootModel, undefined, layout.params, subSubLayoutMessages);
                    this.connection.sendMultiple(subSubLayoutMessages);
                    yield this.createSubLayouts(subSubLayouts);
                }
            }
        });
    }
    onInitLayoutMessage(msg) {
        const data = this.layoutParameterMap.get(msg.uid);
        const layouter = (data !== undefined) ? this.layouters.get(data.params.layouterId) : undefined;
        if (layouter !== undefined)
            layouter.initLayout(msg);
    }
    onDestroyLayoutMessage(msg) {
        const data = this.layoutParameterMap.get(msg.uid);
        const layouter = (data !== undefined) ? this.layouters.get(data.params.layouterId) : undefined;
        if (layouter !== undefined)
            layouter.destroyLayout(msg);
    }
    onEnterRoom(roomModel) {
        this.currentRoomModel = roomModel;
        const data = this.layoutParameterMap.get(roomModel.getUID());
        const layouterParams = (data !== undefined) ? data.params : undefined;
        const layouter = this.getLayouterForRoom(roomModel, layouterParams);
        if (layouter === undefined)
            return;
        const messages = [];
        layouter.onEnterRoom(messages, roomModel);
        if (messages.length > 0)
            this.connection.sendMultiple(messages);
    }
    onExitRoom(roomModel) {
        this.currentRoomModel = undefined;
        const data = this.layoutParameterMap.get(roomModel.getUID());
        const layouterParams = (data !== undefined) ? data.params : undefined;
        const layouter = this.getLayouterForRoom(roomModel, layouterParams);
        if (layouter !== undefined) {
            const messages = [];
            layouter.onExitRoom(messages, roomModel);
            if (messages.length > 0)
                this.connection.sendMultiple(messages);
        }
    }
    getLayouterForRoom(roomModel, layouterParams) {
        return (layouterParams !== undefined) ? this.layouters.get(layouterParams.layouterId) : this.layouters.get(roomModel.getName());
    }
}
